import React, { useState, useEffect } from 'react';
import { Plus, X, ArrowUpDown, Search, Trash2, Edit2, Power } from 'lucide-react';
import { employeesAPI } from "../../services/api";
import { toast } from "react-toastify";
import Delete from "../common/Delete";
import CreateUserModal from "./CreateUserModal";
import EditUserModal from "./EditUserModal";
import ReassignModal from './ReassignModal';

const User = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [reassignEmployee, setReassignEmployee] = useState(null);

  const [visibleColumns] = useState(() => {
    const saved = localStorage.getItem('userViewColumns');
    return saved ? JSON.parse(saved) : [
      'name', 'email', 'phoneNumber', 'roles', 'status', 'createdAt'
    ];
  });

  const allColumns = [
    { key: 'name', label: 'Name', required: true },
    { key: 'email', label: 'Email', required: true },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'roles', label: 'Role' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Created Date' }
  ];

  const [filters, setFilters] = useState({
    role: null,
    status: null,
    firstName: '',
    lastName: '',
    email: ''
  });

  const roleOptions = [
    { value: 'ATS', label: 'ATS' },
    { value: 'CRM', label: 'CRM' },
    { value: 'INVOICE', label: 'Invoice' },
    { value: 'USER_MANAGEMENT', label: 'User Management' }
  ];

  const statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' }
  ];

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const response = await employeesAPI.getAllEmployees();
      let usersData = Array.isArray(response.data) ? response.data : response.data || [];
      setUsers(usersData);
      setFilteredUsers(usersData);
      toast.success(`Loaded ${usersData.length} employees`);
    } catch (error) {
      console.error('Error fetching employees:', error);
      
      // Provide specific error messages
      if (error.response?.status === 403) {
        setAccessDenied(true);
        toast.error('Access Denied: You do not have permission to view employees.');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 500) {
        toast.error('Server error: Failed to fetch employees. Please try again later.');
      } else {
        toast.error('Failed to fetch employees: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Attempt to fetch users; backend enforces permission with 403 if unauthorized
    // When 403 received, interceptor will sync profile and user can retry
    fetchUsers();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...users];

    // Filter by role
    if (filters.role) {
      filtered = filtered.filter(u => Array.isArray(u.roles) && u.roles.includes(filters.role.value));
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(u => u.status === filters.status.value);
    }

    // Text filters - for employee name (not firstName/lastName)
    if (filters.firstName) {
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(filters.firstName.toLowerCase()) ||
        u.email?.toLowerCase().includes(filters.firstName.toLowerCase())
      );
    }
    if (filters.email) {
      filtered = filtered.filter(u => 
        u.email?.toLowerCase().includes(filters.email.toLowerCase())
      );
    }

    // Search query
    if (searchQuery) {
      filtered = filtered.filter(u =>
        u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.phoneNumber?.toString().includes(searchQuery)
      );
    }

    setFilteredUsers(filtered);
  }, [filters, users, searchQuery]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      role: null,
      status: null,
      firstName: '',
      lastName: '',
      email: ''
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredUsers.map(user => user.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleDelete = async () => {
    try {
      if (selectedRows.length === 1) {
        await employeesAPI.deleteEmployee(selectedRows[0]);
      } else {
        // Bulk delete - loop through selected rows
        for (const employeeId of selectedRows) {
          await employeesAPI.deleteEmployee(employeeId);
        }
      }
      toast.success('Employee(s) deleted successfully');
      setSelectedRows([]);
      setShowDeleteModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting employees:', error);
      
      // Provide specific error messages
      let errorMessage = 'Failed to delete employee(s)';
      if (error.response?.status === 404) {
        errorMessage = 'Employee not found.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete employees.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleStatusToggle = async (employeeId, currentStatus, employeeName) => {
    if (currentStatus === 'ACTIVE') {
      try {
        const countRes = await employeesAPI.getAssignedCount(employeeId);
        const counts = countRes.data;
        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        if (total > 0) {
          setReassignEmployee({ id: employeeId, name: employeeName });
          return;
        }
      } catch (e) {
        console.error(e);
      }
    }
    try {
      const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await employeesAPI.updateEmployeeStatus(employeeId, newStatus);
      toast.success(`Employee ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating employee status:', error);
      let errorMessage = 'Failed to update employee status';
      if (error.response?.status === 404) errorMessage = 'Employee not found.';
      else if (error.response?.status === 403) errorMessage = 'You do not have permission.';
      else if (error.response?.status === 401) errorMessage = 'Session expired. Please login again.';
      else if (error.response?.status === 500) errorMessage = 'Server error. Please try again later.';
      else if (error.response?.data?.message) errorMessage = error.response.data.message;
      toast.error(errorMessage);
    }
  };


  const handleEdit = (user) => {
    setSelectedUserForEdit(user);
    setShowEditModal(true);
  };

  const getStatusColor = (status) => {
    return status === 'ACTIVE' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users</h1>
            <p className="text-sm text-gray-500 mt-1">
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus size={16} />
              Add User
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedRows.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-md flex items-center justify-between">
            <span className="text-sm text-blue-900 font-medium">
              {selectedRows.length} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-3 py-1 text-sm text-red-700 hover:bg-red-100 rounded"
              >
                Delete
              </button>
              <button
                onClick={() => setSelectedRows([])}
                className="p-1 text-blue-700 hover:bg-blue-100 rounded"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="p-4 border-b bg-gray-50">
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="flex items-center gap-3">
            {/* Role Filter */}
            <div className="w-40">
              <select
                value={filters.role?.value || ''}
                onChange={(e) => {
                  const selected = roleOptions.find(r => r.value === e.target.value);
                  handleFilterChange('role', selected || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Roles</option>
                {roleOptions.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="w-40">
              <select
                value={filters.status?.value || ''}
                onChange={(e) => {
                  const selected = statusOptions.find(s => s.value === e.target.value);
                  handleFilterChange('status', selected || null);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Status</option>
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {(filters.role || filters.status || filters.firstName || filters.lastName || filters.email) && (
              <button 
                onClick={clearAllFilters}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded-md"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between p-4 border-b gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {accessDenied ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-600">
            <p className="text-lg font-semibold">Access Denied</p>
            <p className="text-sm mt-2 text-center max-w-md">
              You do not have permission to view users. Your permissions are being synced from the server.
            </p>
            <button
              onClick={() => fetchUsers()}
              disabled={loading}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Retrying...' : 'Retry'}
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <p className="text-lg font-medium">No users found</p>
            <p className="text-sm mt-1">Try adjusting your filters or add a new user</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="w-12 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                {allColumns
                  .filter(col => visibleColumns.includes(col.key))
                  .map((column) => (
                    <th
                      key={column.key}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        <ArrowUpDown size={14} className="text-gray-400" />
                      </div>
                    </th>
                  ))}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className={`group hover:bg-gray-50 ${
                    selectedRows.includes(user.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(user.id)}
                      onChange={() => handleRowSelect(user.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  {allColumns
                    .filter(col => visibleColumns.includes(col.key))
                    .map((column) => (
                      <td key={column.key} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {column.key === 'status' ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user[column.key])}`}>
                            {user[column.key]}
                          </span>
                        ) : column.key === 'roles' ? (
                          <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {(user.roles || []).map(r => (
                              <span key={r} className="px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: '#3B5A67', color: '#fff' }}>{r}</span>
                            ))}
                          </span>
                        ) : column.key === 'createdAt' ? (
                          formatDate(user[column.key])
                        ) : (
                          user[column.key] || '-'
                        )}
                      </td>
                    ))}
                  <td className="px-4 py-4 whitespace-nowrap sticky right-0 bg-white group-hover:bg-gray-50 transition-colors shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.04)]" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        title="Edit user"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleStatusToggle(user.id, user.status, user.name)}
                        className={`p-1 rounded ${
                          user.status === 'ACTIVE'
                            ? 'text-yellow-600 hover:bg-yellow-100'
                            : 'text-green-600 hover:bg-green-100'
                        }`}
                        title={user.status === 'ACTIVE' ? 'Inactivate' : 'Activate'}
                      >
                        <Power size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRows([user.id]);
                          setShowDeleteModal(true);
                        }}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        title="Delete user"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreate={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
        />
      )}

      {showEditModal && selectedUserForEdit && (
        <EditUserModal
          user={selectedUserForEdit}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUserForEdit(null);
          }}
          onSave={() => {
            setShowEditModal(false);
            setSelectedUserForEdit(null);
            fetchUsers();
          }}
        />
      )}

      {showDeleteModal && (
        <Delete
          count={selectedRows.length}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
      )}

      {reassignEmployee && (
        <ReassignModal
          employee={reassignEmployee}
          onClose={() => setReassignEmployee(null)}
          onConfirm={() => { setReassignEmployee(null); fetchUsers(); toast.success('Records reassigned and employee deactivated.'); }}
        />
      )}
    </div>
  );
};

export default User;
